/**
 * RoomPassword: 비공개방 비밀번호 입력 다이얼로그.
 *
 * v1 원본: RenewalComponents/Password/RoomPassword.js
 * 마크업/CSS 클래스를 v1과 **동일**하게 유지.
 *
 * 기능:
 * - 회의 ID 표시 (conference_id → 3자리 구분)
 * - 비밀번호 입력 → joinCode와 비교
 * - 일치 시 onPasswordCorrect(password) 콜백 호출
 * - 불일치 시 스낵바(alert) 표시
 */
import React, { useState, useCallback, useEffect } from 'react';
import SNSnackbar from '@luna-sena/sena-snackbar';
import { useAppSelector } from '@/store/hooks';
import { isMasterUser } from '@/store/slices/masterSlice';

interface RoomPasswordProps {
  /** 비밀번호 일치 시 호출 — password를 전달 */
  onPasswordCorrect: (password: string) => void;
  /** 다이얼로그 닫기 (SETUP으로 복귀) */
  onClose: () => void;
}

const RoomPassword: React.FC<RoomPasswordProps> = ({ onPasswordCorrect, onClose }) => {
  const local = useAppSelector((s) => s.local);
  const masterUsers = useAppSelector((s) => s.master.masterUsers);

  const [password, setPassword] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '' });

  // 회의 ID 포맷: 123456789 → 123-456-789
  const conferenceId = local.conferenceId
    ? local.conferenceId.replace(/(\d{3})(?=\d)/g, '$1-')
    : '';

  const isMaster = isMasterUser(masterUsers, local.userId)
    || (!!local.userId && local.creatorInfo?.portal_id === local.userId);

  // 마스터/방생성자가 판정 경합으로 이 다이얼로그까지 도달한 경우 —
  // 기존에는 handleConfirm이 early return 되어 확인 버튼이 먹통이었다. 즉시 통과시킨다.
  useEffect(() => {
    if (isMaster) onPasswordCorrect(local.joinCode || '');
  }, [isMaster]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleConfirm = useCallback(() => {
    if (isMaster) {
      onPasswordCorrect(local.joinCode || '');
      return;
    }

    // joinCode 미수신(방 정보 조회 지연/실패) 상태에서는 비교 자체가 무의미하므로 안내만 한다.
    if (!local.joinCode) {
      setSnackbar({ open: true, message: '회의 정보를 불러오는 중입니다. 잠시 후 다시 시도해주세요.' });
      return;
    }

    const isPasswordCorrect = local.joinCode === password;
    if (isPasswordCorrect) {
      onPasswordCorrect(password);
    } else {
      setSnackbar({ open: true, message: '회의 암호가 맞지 않습니다.' });
    }
  }, [isMaster, local.joinCode, password, onPasswordCorrect]);

  const handleKeyPress = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        handleConfirm();
      }
    },
    [handleConfirm],
  );

  return (
    <div className="rtcNewDialog">
      <button
        type="button"
        className="rtcNewDialog__closeBtn"
        onClick={onClose}
      >
        <span className="blind">닫기</span>
      </button>
      <div
        className="rtcNewDialog__wrap"
        style={{ position: 'absolute', top: '50%', marginTop: '-140px' }}
      >
        <h2 className="rtcNewDialog__title">회의 암호를 입력해주세요.</h2>
        <dl className="rtcNewDialog_pw_meetingNum">
          <dt>회의 ID</dt>
          <dd>{conferenceId}</dd>
        </dl>
        <div className="rtcNewDialog_pw_inpBox">
          <input
            type="text"
            className="rtcNewDialog_pw_inp"
            placeholder="암호를 입력해주세요."
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onKeyDown={handleKeyPress}
          />
          {password && (
            <button
              type="button"
              className="rtcNewDialog_pw_inpDelbtn"
              onClick={() => setPassword('')}
            >
              <span className="blind">삭제</span>
            </button>
          )}
        </div>
        <div className="rtcNewDialog__footer rtcCustoms_roomPwFooter">
          <button
            type="button"
            className="common_round_btn rtcNewBtn"
            onClick={handleConfirm}
          >
            참가
          </button>
        </div>
      </div>

      <SNSnackbar
        open={snackbar.open}
        type="error"
        message={snackbar.message}
        autoHideDuration={3000}
        onRequestClose={() => setSnackbar({ open: false, message: '' })}
      />
    </div>
  );
};

export default RoomPassword;
